import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Beaker, Leaf, ShieldCheck, Sparkles } from "lucide-react";
import { Reveal } from "@/components/motion/Reveal";
import { BlogByline } from "@/components/BlogByline";
import { ReadWithLlm } from "@/components/ReadWithLlm";
import { buildBlogHead } from "@/lib/blogMeta";
import { RelatedReading } from "@/components/RelatedReading";
import { References } from "@/components/References";
import { ClusterLinks } from "@/components/ClusterLinks";
import { AuthorityFlow } from "@/components/AuthorityFlow";
import { relatedArticles } from "@/lib/clusters";
import heroAsset from "@/assets/blog/fatty-liver-diet.jpg.asset.json";
import { ClinicalProductPitch } from "@/components/ClinicalProductPitch";

const URL = "https://truwe.in/blog/fatty-liver-diet-plan-india";
const TITLE = "Fatty Liver (NAFLD) Diet Plan. Indian Foods That Help and Hurt (2026) | TruWe";

export const Route = createFileRoute("/blog/fatty-liver-diet-plan-india")({
  component: Article,
  head: () =>
    buildBlogHead({
      slug: "fatty-liver-diet-plan-india",
      title: TITLE,
      description:
        "A practical Indian fatty liver (NAFLD) diet plan, foods that help, foods that hurt, sample day and the evidence-based supplement stack that supports reversal.",
      ogTitle: "Fatty Liver Diet Plan. Indian Foods That Help and Hurt",
      ogDescription:
        "What to eat, what to cut, and a realistic Indian NAFLD day plan, grounded in the research, not the wellness internet.",
      headline: "Fatty Liver (NAFLD) Diet Plan for Indians",
      breadcrumbName: "Fatty Liver Diet (India)",
      datePublished: "2026-07-12",
      dateModified: "2026-07-25",
      ogImage: `https://truwe.in${heroAsset.url}`,
      faqs: [
        {
          q: "Can Indian food fit a fatty liver diet?",
          a: "Absolutely, traditional Indian food is friendlier to the liver than modern packaged food. Millets, dals, sabzis in cold-pressed oil, curd and small amounts of ghee are all fine. The problems are sugar, maida, seed oils and portion drift.",
        },
        {
          q: "Is ghee bad for fatty liver?",
          a: "No, in moderation, 1-2 tsp/day of A2 ghee is neutral to positive. The saturated fat that damages the liver is the one made inside the liver from excess fructose and refined carbs (de novo lipogenesis).",
        },
        {
          q: "Can I eat rice with fatty liver?",
          a: "Small portions of parboiled or hand-pounded rice paired with dal, sabzi and curd yes. White rice as the largest thing on your plate no. Swap half the rice for millets or add extra dal to lower the glycemic load.",
        },
        {
          q: "Do I need to give up alcohol completely?",
          a: "For a 90-day reset, yes. After LFTs normalise, ≤3 drinks/week is a reasonable ceiling. NAFLD progression accelerates sharply above that.",
        },
      ],
    }),
});

function Article() {
  return (
    <article className="bg-background pb-24 pt-28 md:pt-36">
      <div className="mx-auto max-w-3xl px-4 md:px-8">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand">
            Clinical Library · Liver & Gut
          </p>
          <h1 className="mt-3 font-display text-4xl font-extrabold leading-tight tracking-tight text-brand-deep md:text-5xl">
            Fatty liver (NAFLD) diet plan. Indian foods that help and hurt
          </h1>
          <BlogByline date="2026-07-12" readingTime="10 min read" />
          <div className="mt-4">
            <ReadWithLlm title={TITLE} url={URL} kind="article" />
          </div>
          <p className="mt-4 text-base text-muted-foreground md:text-lg">
            Fatty liver isn't a fat problem. It's a fructose-and-refined-carb problem.
            The liver converts excess sugar to fat and packs it into hepatocytes. The
            good news: Indian food, done properly, is one of the better cuisines to
            reverse it in.
          </p>
        <figure className="mt-8 overflow-hidden rounded-3xl border border-border">
            <img
              src={heroAsset.url}
              alt="Fatty liver diet plan India, dal, greens and healthy fats"
              width={1200}
              height={630}
              loading="eager"
              className="h-auto w-full"
            />
          </figure>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-bold text-brand-deep md:text-3xl">
            The two principles
          </h2>
          <ul className="mt-4 space-y-3 text-base text-foreground/85">
            <li>
              <strong>Take pressure off the liver:</strong> less fructose, refined
              carbs, seed oils and alcohol.
            </li>
            <li>
              <strong>Give it what it needs:</strong> protein at every meal, cruciferous
              vegetables, choline sources, coffee, and enough magnesium and B-vitamins
              for Phase II detox.
            </li>
          </ul>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-bold text-brand-deep md:text-3xl">
            Foods that help
          </h2>
          <div className="mt-5 space-y-4">
            {[
              {
                name: "Cruciferous vegetables",
                note: "Broccoli, cauliflower, patta gobhi, mooli, sarson. Sulforaphane upregulates Phase II detox enzymes. Aim for a fist-sized portion daily.",
              },
              {
                name: "Bitter greens",
                note: "Methi, karela, palak. Karela is one of the few foods with human trial data for lower fasting glucose, a NAFLD tailwind.",
              },
              {
                name: "Dals & legumes",
                note: "Moong, masoor, chana, rajma. Slow-release carbs + protein = low insulin spike. Pair with a sabzi and small rice.",
              },
              {
                name: "Coffee, 2-3 cups a day",
                note: "One of the strongest hepatoprotective foods in cohort studies. Black or with a splash of milk. No sugar, no flavoured syrups.",
              },
              {
                name: "Fatty fish 2× / week",
                note: "Rohu, mackerel, salmon. EPA/DHA lower hepatic triglycerides in NAFLD trials. Vegetarian? Chia + flax + an algal DHA capsule.",
              },
              {
                name: "Eggs (choline)",
                note: "The liver needs choline to package and ship out triglycerides. Two whole eggs a day covers ~60% of the daily requirement.",
              },
              {
                name: "Curd / dahi",
                note: "Probiotic support for the gut-liver axis. Home-set dahi > flavoured 'yogurt drinks' loaded with sugar.",
              },
              {
                name: "Ragi, bajra, jowar",
                note: "Swap half your rice/roti for millets. Higher magnesium, higher fibre, lower glycemic load.",
              },
            ].map((row) => (
              <div key={row.name} className="rounded-2xl border border-border bg-card p-5 shadow-sm">
                <div className="flex items-center gap-2">
                  <Beaker className="h-4 w-4 text-brand" />
                  <h3 className="font-display text-lg font-semibold text-brand-deep">{row.name}</h3>
                </div>
                <p className="mt-2 text-sm text-foreground/80">{row.note}</p>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-bold text-brand-deep md:text-3xl">
            Foods that hurt (be honest)
          </h2>
          <ul className="mt-4 space-y-3 text-base text-foreground/85">
            <li>
              <strong>Sugar-sweetened drinks</strong> sweetened chai, cold coffees,
              packaged juices, soft drinks. The single biggest driver of NAFLD in
              Indian adults.
            </li>
            <li>
              <strong>Maida</strong> biscuits, bakery, samosas, pav, most restaurant
              breads. Rapid glucose spike → hepatic de novo lipogenesis.
            </li>
            <li>
              <strong>Deep-fried seed oils</strong> sunflower, soybean, refined palm.
              High in omega-6 and lipid peroxides. Switch to ghee or cold-pressed
              mustard/coconut for cooking.
            </li>
            <li>
              <strong>Alcohol</strong> during a 90-day reset, cut to zero. After,
              cap at 3 drinks/week.
            </li>
            <li>
              <strong>Ultra-processed snacks</strong> chips, packaged namkeens, cream
              biscuits. Fructose + seed oil + salt is the exact NAFLD accelerator.
            </li>
          </ul>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-bold text-brand-deep md:text-3xl">
            A realistic Indian day
          </h2>
          <div className="mt-4 space-y-4 text-base text-foreground/85">
            <div>
              <p className="font-semibold text-brand-deep">Break the fast (9-10 AM):</p>
              <p>
                Two-egg bhurji + one bajra roti with ghee, or besan chilla + dahi. Black
                coffee.
              </p>
            </div>
            <div>
              <p className="font-semibold text-brand-deep">Lunch (1-2 PM):</p>
              <p>
                Small bowl of parboiled rice or one jowar roti + dal + sabzi (with a
                cruciferous vegetable) + a cup of curd + salad with cold-pressed oil.
              </p>
            </div>
            <div>
              <p className="font-semibold text-brand-deep">Snack (5 PM):</p>
              <p>
                A handful of almonds and roasted chana, or one small fruit. Green tea.
              </p>
            </div>
            <div>
              <p className="font-semibold text-brand-deep">Dinner (by 8 PM):</p>
              <p>
                Grilled fish or paneer bhurji + big sabzi + a small ragi roti. Skip the
                rice at dinner.
              </p>
            </div>
            <p className="text-sm text-muted-foreground">
              12-14 hour overnight fast, no snacks after 8 PM. This alone drops liver
              fat measurably in 4 weeks.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <div className="mt-14 rounded-3xl border border-brand/15 bg-gradient-to-br from-brand/5 via-background to-brand-deep/5 p-8 md:p-10">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-brand">
              <Sparkles className="h-4 w-4" /> The TruWe formulation
            </div>
            <h2 className="mt-3 font-display text-2xl font-bold text-brand-deep md:text-3xl">
              Liver Biome Plus, the ingredient support your diet can't cover
            </h2>
            <p className="mt-3 text-base text-foreground/85">
              Diet does the heavy lifting; Liver Biome Plus closes the gap. Liposomal
              <strong> silymarin, curcumin and alpha-lipoic acid</strong>, plus
              <strong> NAC and a multi-strain probiotic</strong> the ingredients with
              actual RCTs in NAFLD, at doses your food will not deliver.
            </p>
            <div className="mt-5 grid gap-3 sm:grid-cols-3">
              {[
                { icon: Leaf, label: "Liposomal delivery" },
                { icon: ShieldCheck, label: "Third-party tested" },
                { icon: Beaker, label: "Research-backed doses" },
              ].map(({ icon: Icon, label }) => (
                <div
                  key={label}
                  className="flex items-center gap-2 rounded-xl bg-background/70 px-3 py-3 text-sm text-foreground/85 ring-1 ring-border"
                >
                  <Icon className="h-4 w-4 text-brand" />
                  {label}
                </div>
              ))}
            </div>
            <Link
              to="/products/$handle"
              params={{ handle: "liver-biome-plus" }}
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-semibold text-white shadow-luxury transition hover:bg-brand-deep"
            >
              Explore Liver Biome Plus <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <ClinicalProductPitch cluster="liver-gut" />
          <ClusterLinks cluster="liver-gut" />
          <AuthorityFlow cluster="liver-gut" />
          <References slug="fatty-liver-diet-plan-india" />
          <RelatedReading articles={relatedArticles("fatty-liver-diet-plan-india")} />
        </Reveal>

        <Reveal delay={0.05}>
          <p className="mt-12 text-xs text-muted-foreground">
            Educational only. Personalise with a registered dietitian, especially if you
            have diabetes, kidney disease or take blood-thinners.
          </p>
        </Reveal>
      </div>
    </article>
  );
}
